/**
 * User model interfaces matching the User API specification
 * 
 * @remarks
 * These models correspond to the User API endpoints at `/api/users`
 * All interfaces are designed to match the API response structure exactly.
 * 
 * @see USER_API_REFERENCE.md for complete API documentation
 */

import { BaseEntity, OrganizationRef, OrganizationRole, GroupSummary } from './base';
import { GroupDetailed } from './group.model';

/**
 * Supported currency codes
 */
export type Currency = 'USD' | 'EUR' | 'GBP' | 'INR' | 'CAD' | 'AUD' | 'JPY' | 'CNY';

/**
 * Supported language codes
 */
export type Language = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'zh' | 'ja' | 'ko' | 'hi';

/**
 * Authentication provider used to create the account
 */
export type AuthProvider = 'email' | 'google' | 'apple';

/**
 * Enriched organization shape returned by GET /api/users/profile/detailed
 *
 * @remarks
 * Extends OrganizationRef with timestamps and makes `userRole` and `groups`
 * required (both are always present in the detailed-profile response).
 * Because this extends OrganizationRef, it is assignable anywhere
 * OrganizationRef[] is expected.
 */
export interface OrganizationDetailed extends OrganizationRef {
  /** ISO 8601 creation timestamp */
  createdAt: string;
  /** ISO 8601 last-updated timestamp */
  updatedAt: string;
  /** User's membership role in this organization (always present in detailed response) */
  userRole: OrganizationRole;
  /** Groups the user belongs to within this organization (always present in detailed response) */
  groups: GroupSummary[];
}

/**
 * Core User model matching API schema
 * 
 * @remarks
 * This model represents a user with non-populated organization references.
 * For the detailed version with populated organizations, use UserDetailed.
 * 
 * The firebaseUid field is never included in API responses for security.
 */
export interface User extends BaseEntity {
  /**
   * User's email address (unique, lowercase)
   */
  email: string;

  /**
   * User's display name (max 50 characters)
   */
  name: string;

  /**
   * Whether the user's email has been verified
   */
  emailVerified: boolean;

  /**
   * Optional URL to user's profile photo
   */
  photoURL?: string;

  /**
   * Optional phone number
   */
  phoneNumber?: string;

  /**
   * Optional biography/description (max 200 characters)
   */
  bio?: string;

  /**
   * Optional date of birth (ISO 8601 format)
   */
  dateOfBirth?: string;

  /**
   * Preferred currency for financial transactions
   * @default 'USD'
   */
  currency: Currency;

  /**
   * User's timezone
   * @default 'UTC'
   */
  timezone: string;

  /**
   * Preferred language for UI and communications
   * @default 'en'
   */
  language: Language;

  /**
   * User roles for system-wide permissions
   * @default ['user']
   */
  roles: string[];

  /**
   * Whether the user account is active
   * Inactive users cannot log in
   * @default true
   */
  isActive: boolean;

  /**
   * Timestamp of the user's last login
   */
  lastLoginAt?: string;

  /**
   * Authentication provider used to sign in
   * @default 'email'
   */
  authProvider?: AuthProvider;

  /**
   * Array of organization IDs the user belongs to
   * Use UserDetailed for populated organization data
   */
  organizations: string[];

  /**
   * Groups the user belongs to across all organizations.
   * Returned by GET /api/users/me for backward compatibility.
   * Each item contains only _id and name.
   * For org-scoped group membership use organizations[].groups instead.
   */
  groups?: GroupSummary[];

  /** ISO 8601 timestamp when the user accepted the Terms of Agreement */
  tcAcceptedAt?: string;
}

/**
 * User with populated organization references and group details
 *
 * @remarks
 * Returned by GET /api/users/profile/detailed.
 * Contains enriched organization details (with timestamps, confirmed role, and
 * group membership) instead of raw IDs.
 * `groups` replaces the root-level GroupSummary array with full GroupDetailed objects.
 */
export interface UserDetailed extends Omit<User, 'organizations' | 'groups'> {
  /**
   * Populated organization references including timestamps, role, and group membership.
   * Assignable to OrganizationRef[] for backward-compatible component inputs.
   */
  organizations: OrganizationDetailed[];

  /**
   * Full group details for all groups the user belongs to.
   * Use GET /api/groups/:id for member roster data.
   */
  groups: GroupDetailed[];
}

/**
 * Data Transfer Object for creating a new user
 * 
 * @remarks
 * Used in POST /api/users (registration endpoint)
 * This is a public endpoint that doesn't require authentication.
 * 
 * **Organization Linking:**
 * - Provide `invitationToken` to join an existing organization
 * - Provide `orgName` and `orgCode` to create a new organization
 * - User becomes Admin of organizations they create
 */
export interface CreateUserDto {
  /**
   * Firebase authentication UID (required, exactly 28 characters)
   */
  firebaseUid: string;

  /**
   * User's email address (required, valid email format)
   */
  email: string;

  /**
   * User's display name (required)
   */
  name: string;

  /**
   * Whether email has been verified by Firebase
   */
  emailVerified?: boolean;

  /**
   * URL to user's profile photo
   */
  photoURL?: string;

  /**
   * User's phone number
   */
  phoneNumber?: string;

  /**
   * User roles (optional, defaults to ['user'])
   */
  roles?: string[];

  /**
   * Optional invitation token to join an existing organization
   * If provided, user will be added to the organization with the specified role
   */
  invitationToken?: string;

  /**
   * Optional organization name for creating a new organization during registration
   * Must be provided along with orgCode
   */
  orgName?: string;

  /**
   * Optional organization code for creating a new organization during registration
   * Must be provided along with orgName
   */
  orgCode?: string;

  /**
   * Authentication provider used to create the account
   * Defaults to 'email' on the server if omitted
   */
  authProvider?: AuthProvider;

  /**
   * Subscription plan slug to associate with the new organization.
   * Defaults to 'starter' on the server if omitted.
   * e.g. 'starter' | 'pro' | 'enterprise'
   */
  planSlug?: string;

  /** ISO 8601 timestamp when the user accepted the Terms of Agreement */
  tcAcceptedAt?: string;
}

/**
 * Data Transfer Object for updating current user's profile
 * 
 * @remarks
 * Used in PUT /api/users/me
 * Users can only update their own profile.
 * All fields are optional - only provide fields to update.
 */
export interface UpdateUserProfileDto {
  /**
   * Updated display name (max 50 characters)
   */
  name?: string;

  /**
   * Updated phone number
   */
  phoneNumber?: string;

  /**
   * Updated biography (max 200 characters)
   */
  bio?: string;

  /**
   * Updated date of birth (ISO 8601 format)
   */
  dateOfBirth?: string;

  /**
   * Updated currency preference
   */
  currency?: Currency;

  /**
   * Updated timezone
   */
  timezone?: string;

  /**
   * Updated language preference
   */
  language?: Language;

  /** ISO 8601 timestamp when the user accepted the Terms of Agreement */
  tcAcceptedAt?: string;
}

/**
 * Data Transfer Object for admin user updates
 * 
 * @remarks
 * Used in PUT /api/users/:id (Admin only)
 * Allows admins to update any user, including roles and active status.
 * All fields are optional - only provide fields to update.
 */
export interface UpdateUserDto extends UpdateUserProfileDto {
  /**
   * Updated user roles (admin only)
   */
  roles?: string[];

  /**
   * Updated active status (admin only)
   * Set to false to disable user account
   */
  isActive?: boolean;
}

/**
 * Query parameters for user search and listing
 * 
 * @remarks
 * Used in GET /api/users (Admin only)
 * Supports pagination and text search.
 */
export interface UserQueryParams {
  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Number of items per page
   * @default 10
   */
  limit?: number;

  /**
   * Search query to filter by name or email
   * Performs case-insensitive partial matching
   */
  search?: string;
}

/**
 * API response wrapper for single user
 */
export interface UserResponse {
  status: 'success';
  data: {
    user: User;
  };
  message?: string;
}

/**
 * API response wrapper for detailed user
 */
export interface UserDetailedResponse {
  status: 'success';
  data: {
    user: UserDetailed;
  };
}

/**
 * API response wrapper for user list with pagination
 */
export interface UserListResponse {
  status: 'success';
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

/**
 * API response wrapper for user creation
 */
export interface CreateUserResponse {
  status: 'success';
  message: 'User created successfully';
  data: {
    user: User;
  };
}
